import { useEffect, useState, type CSSProperties } from 'react'
import { branding } from '@/shared/config/branding'
import { hideSplashScreen } from '@/shared/lib/platform/splashScreen'
import { isScreenshotFixtureMode } from '@/shared/lib/screenshotMode'

/** Remove markup estático de pré-hidratação (index.html), fora da árvore React. */
const removePreSplashMarkup = () => {
  document.getElementById('pre-splash')?.remove()
}

const SPLASH_SESSION_FLAG = 'synap:splash:shown'

/**
 * Duração do fade-out CSS (`synap-splash-dismiss` em src/index.css).
 * O splash some assim que `ready` fica true — sem duração mínima artificial.
 */
export const DISMISS_MS = 250

interface SplashScreenProps {
  /** Quando true, inicia o fade-out imediatamente (sem atraso mínimo). */
  ready?: boolean
  /** Pula o splash imediatamente (ex.: navegações internas já hidratadas). */
  skip?: boolean
}

/** Rede de sinapses sobre o cérebro (coords no viewBox 1632x1704 do logo).
 *  Nós disparam e conexões se desenham em sequência — "conexão neural acontecendo".
 *  Mesma composição do pre-splash em index.html (evita corte no handoff React). */
const SYNAPSE_NODES: ReadonlyArray<{ cx: number; cy: number; r: number; d: number }> = [
  { cx: 250, cy: 620, r: 26, d: 90 },
  { cx: 470, cy: 560, r: 30, d: 160 },
  { cx: 690, cy: 640, r: 34, d: 240 },
  { cx: 900, cy: 520, r: 30, d: 310 },
  { cx: 1180, cy: 610, r: 26, d: 380 },
  { cx: 780, cy: 900, r: 32, d: 280 },
  { cx: 560, cy: 980, r: 26, d: 350 },
  { cx: 1010, cy: 960, r: 26, d: 410 },
]

const SYNAPSE_EDGES: ReadonlyArray<{ x1: number; y1: number; x2: number; y2: number; d: number }> = [
  { x1: 250, y1: 620, x2: 470, y2: 560, d: 60 },
  { x1: 470, y1: 560, x2: 690, y2: 640, d: 130 },
  { x1: 690, y1: 640, x2: 900, y2: 520, d: 210 },
  { x1: 900, y1: 520, x2: 1180, y2: 610, d: 280 },
  { x1: 690, y1: 640, x2: 780, y2: 900, d: 180 },
  { x1: 780, y1: 900, x2: 560, y2: 980, d: 260 },
  { x1: 780, y1: 900, x2: 1010, y2: 960, d: 320 },
]

const edgeLen = (e: { x1: number; y1: number; x2: number; y2: number }): number =>
  Math.round(Math.hypot(e.x2 - e.x1, e.y2 - e.y1))

export const SplashScreen = ({ ready = false, skip = false }: SplashScreenProps) => {
  const [visible, setVisible] = useState(() => {
    if (skip || isScreenshotFixtureMode()) return false
    try {
      return sessionStorage.getItem(SPLASH_SESSION_FLAG) !== '1'
    } catch {
      return true
    }
  })
  const [dismissing, setDismissing] = useState(false)

  useEffect(() => {
    removePreSplashMarkup()
  }, [])

  // Esconde o splash nativo Capacitor após a 1ª frame (web: no-op).
  // Também quando o splash in-app não aparece (skip / flag / screenshot), para não ficar preso.
  useEffect(() => {
    let cancelled = false
    const rafId = requestAnimationFrame(() => {
      if (cancelled) return
      void hideSplashScreen().catch(() => {
        /* plugin indisponível / falha nativa — splash web cobre a viewport */
      })
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(rafId)
    }
  }, [])

  useEffect(() => {
    if (!visible) return
    if (!ready) return

    // setTimeout(0): inicia o fade na próxima macrotarefa (sem mínimo artificial;
    // evita setState síncrono no efeito — react-hooks/set-state-in-effect).
    const dismissTimer = window.setTimeout(() => {
      setDismissing(true)
      try {
        sessionStorage.setItem(SPLASH_SESSION_FLAG, '1')
      } catch {
        /* sessionStorage indisponível — splash some mesmo assim */
      }
    }, 0)

    return () => window.clearTimeout(dismissTimer)
  }, [ready, visible])

  useEffect(() => {
    if (!dismissing) return
    const unmountTimer = window.setTimeout(() => setVisible(false), DISMISS_MS)
    return () => window.clearTimeout(unmountTimer)
  }, [dismissing])

  if (!visible) return null

  const splash = (
    <div
      className="synap-splash"
      data-dismissed={dismissing ? 'true' : undefined}
      role="status"
      aria-label={`Carregando ${branding.appName}`}
    >
      <div className="synap-splash__brain">
        <div className="synap-splash__brain-layer synap-splash__brain-layer--base" />
        <div className="synap-splash__brain-layer synap-splash__brain-layer--energy" />
        <div className="synap-splash__brain-layer synap-splash__brain-layer--sweep" />
        <svg
          className="synap-splash__net"
          viewBox="0 0 1632 1704"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          {SYNAPSE_EDGES.map((e, i) => (
            <path
              key={`e${i}`}
              className="synap-edge"
              style={{ '--len': edgeLen(e), '--d': `${e.d}ms` } as CSSProperties}
              d={`M ${e.x1} ${e.y1} L ${e.x2} ${e.y2}`}
            />
          ))}
          {SYNAPSE_NODES.map((n, i) => (
            <circle
              key={`n${i}`}
              className="synap-node"
              style={{ '--d': `${n.d}ms` } as CSSProperties}
              cx={n.cx}
              cy={n.cy}
              r={n.r}
            />
          ))}
        </svg>
      </div>

      <div className="synap-splash__wordmark">
        <h1>{branding.appName}</h1>
        <p>{branding.slogan}</p>
      </div>
    </div>
  )

  // Dentro da árvore React (#root): position:fixed cobre a viewport sem portal em document.body
  // (evita NotFoundError removeChild quando PostHog ou extensões alteram filhos de body — SYNAP-DOC-2C).
  return splash
}
